import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests';
import { Pagination } from '../../features/Pagination';
import { useUrlPagination } from '../../shared/hooks/useUrlPagination.js';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';
import { EmptyState } from '../../shared/ui/EmptyState';
import { FileSignature, Plus } from 'lucide-react';

const PAGE_SIZE = 20;

const Documents = () => {
  const [documents, setDocuments] = useState({ items: [], totalCount: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [searchValue, setSearchValue] = useState(q);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const { page, offset, pageSize, setPage } = useUrlPagination(PAGE_SIZE);
  const location = useLocation();

  // Черновик в поле поиска следует за URL (назад/вперёд в браузере)
  useEffect(() => {
    setSearchValue(q);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequests.documents
      .get(q, pageSize, offset)
      .then((res) => {
        if (!cancelled) setDocuments(res);
      })
      .catch((e) => console.error('Ошибка загрузки документов:', e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [q, offset, pageSize, reloadKey]);

  const handleSearch = () => {
    const next = searchValue.trim();
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next) params.set('q', next);
      else params.delete('q');
      params.delete('page');
      return params;
    });
  };

  const handleDelete = (id) => {
    apiRequests.documents.delete(id).then(() => {
      if (documents.items?.length === 1 && page > 1) setPage(page - 1);
      else setReloadKey((k) => k + 1);
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск по номеру или имени клиента..."
        createLink={`${location.pathname}/create`}
      />

      <div className="mb-3">
        {loading ? (
          <p className="text-center">Загрузка...</p>
        ) : documents.items?.length === 0 ? (
          q ? (
            <EmptyState
              icon={FileSignature}
              variant="search"
              title="Договор не найден"
              description="Ищем по номеру договора и имени клиента. Проверьте номер или попробуйте часть имени."
            />
          ) : (
            <EmptyState
              icon={FileSignature}
              title="Договоров пока нет"
              description="Заполните реквизиты клиента и условия — договор соберётся по шаблону и будет храниться здесь."
              action={{ label: 'Создать договор', to: `${location.pathname}/create`, icon: Plus }}
            />
          )
        ) : (
          documents.items?.map((doc) => (
            <EntityCard
              key={doc.id}
              title={`Договор №${doc.number}${doc.companyName ? ` - ${doc.companyName}` : ''}`}
              fields={[
                { label: 'Клиент', value: doc.customerName },
                { label: 'Дата', value: formatDate(doc.date) },
              ]}
              editLink={`${location.pathname}/${doc.id}`}
              onDelete={() => handleDelete(doc.id)}
            />
          ))
        )}
      </div>

      <Pagination
        page={page}
        total={documents.totalCount}
        pageSize={pageSize}
        onPageChange={setPage}
      />
    </>
  );
};

export default Documents;
